import React, { useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Users, CreditCard, BookOpen, HelpCircle,
  Image, Settings, BarChart3, Award, GamepadIcon as Gamepad2,
  Bot, MessageSquare, ClipboardCheck, FileText, ToggleLeft,
  ShieldCheck, Shield, Globe, Download,
  Inbox,
  TrendingDown,
  Brain,
  FlaskConical,
  CalendarClock,
  Plug,
  MessageSquareHeart,
  Activity,
  Tag,
  Sparkles,
  Palette,
  Megaphone,
  Mail,
  Briefcase,
  Building2,
  GitBranch,
  Link,
  Target,
  MessageCircle,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { adminFetch } from '../lib/api';
import { ChangelogModal } from './ChangelogModal';
import { useAppVersion } from '../hooks/useAppVersion';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  badgeKey?: string; // maps to notification count key
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navigation: NavSection[] = [
  {
    title: '',
    items: [
      { label: 'Übersicht', path: '/', icon: <LayoutDashboard /> },
    ],
  },
  {
    title: 'Nutzer',
    items: [
      { label: 'Nutzerliste', path: '/users', icon: <Users /> },
      { label: 'Support', path: '/support', icon: <Inbox />, badgeKey: 'support' },
      { label: 'Live-Chat', path: '/support-chat', icon: <MessageCircle />, badgeKey: 'chats' },
      { label: 'Feedback', path: '/beta-feedback', icon: <MessageSquareHeart />, badgeKey: 'feedback' },
    ],
  },
  {
    title: 'Finanzen',
    items: [
      { label: 'Abos', path: '/subscriptions', icon: <CreditCard /> },
      { label: 'Preise', path: '/pricing', icon: <Tag /> },
    ],
  },
  {
    title: 'Content',
    items: [
      { label: 'Kapitel', path: '/chapters', icon: <BookOpen /> },
      { label: 'Fragen', path: '/questions', icon: <HelpCircle /> },
      { label: 'Bilder', path: '/images', icon: <Image /> },
      { label: 'Bildprüfung', path: '/image-review', icon: <Image /> },
      { label: 'Content QA', path: '/content-qa', icon: <ClipboardCheck /> },
      { label: 'Bild-Kategorien', path: '/image-categories', icon: <Image /> },
      { label: 'Bild-Sweep', path: '/image-sweep', icon: <ClipboardCheck /> },
      { label: 'Badges', path: '/badges', icon: <Award /> },
    ],
  },
  {
    title: 'KI-Ops',
    items: [
      { label: 'KI Dashboard', path: '/ai-dashboard', icon: <Bot /> },
      { label: 'Prompts', path: '/ai-prompts', icon: <MessageSquare /> },
      { label: 'A/B-Testing', path: '/prompt-testing', icon: <FlaskConical /> },
    ],
  },
  {
    title: 'Analytics',
    items: [
      { label: 'Lernerfolg', path: '/analytics/learning', icon: <BarChart3 /> },
      { label: 'Simulationen', path: '/analytics/simulations', icon: <ClipboardCheck /> },
    ],
  },
  {
    title: 'Intelligence',
    items: [
      { label: 'Churn-Prediction', path: '/churn', icon: <TrendingDown /> },
      { label: 'Content-Qualitaet', path: '/content-intelligence', icon: <Brain /> },
    ],
  },
  {
    title: 'Gamification',
    items: [
      { label: 'Gamification', path: '/gamification', icon: <Gamepad2 /> },
    ],
  },
  {
    title: 'Marketing',
    items: [
      { label: 'Post erstellen', path: '/marketing/create', icon: <Sparkles /> },
      { label: 'Posts & Kalender', path: '/marketing/posts', icon: <FileText /> },
      { label: 'Kampagnen', path: '/marketing/campaigns', icon: <Megaphone /> },
      { label: 'Learning Engine', path: '/marketing/learnings', icon: <Brain /> },
      { label: 'CI & Branding', path: '/marketing/branding', icon: <Palette /> },
      { label: 'E-Mail Templates', path: '/email-templates', icon: <Mail /> },
      { label: 'Send-Log', path: '/send-log', icon: <FileText /> },
    ],
  },
  {
    title: 'Vertrieb',
    items: [
      { label: 'Übersicht', path: '/sales', icon: <Briefcase /> },
      { label: 'Fahrschulen', path: '/sales/leads', icon: <Building2 /> },
      { label: 'Pipeline', path: '/sales/pipeline', icon: <GitBranch /> },
      { label: 'Kampagnen', path: '/sales/campaigns', icon: <Megaphone /> },
      { label: 'Partner', path: '/sales/partners', icon: <Users /> },
      { label: 'Affiliate', path: '/sales/affiliates', icon: <Link /> },
      { label: 'Sales Copilot', path: '/sales/copilot', icon: <Bot /> },
    ],
  },
  {
    title: 'System',
    items: [
      { label: 'Einstellungen', path: '/settings', icon: <Settings /> },
      { label: 'Rechtstexte', path: '/legal-content', icon: <FileText /> },
      { label: 'Feature Flags', path: '/feature-flags', icon: <ToggleLeft /> },
      { label: 'Rollen', path: '/roles', icon: <ShieldCheck /> },
      { label: 'Sicherheit', path: '/security', icon: <Shield /> },
      { label: 'Audit Log', path: '/audit-log', icon: <FileText /> },
      { label: 'Sprachen', path: '/languages', icon: <Globe /> },
      { label: 'Reports', path: '/reports', icon: <Download /> },
      { label: 'Geplante Reports', path: '/scheduled-reports', icon: <CalendarClock /> },
      { label: 'Integrationen', path: '/integrations', icon: <Plug /> },
      { label: 'System-Status', path: '/system-status', icon: <Activity /> },
      { label: 'Changelog', path: '/changelog', icon: <FileText /> },
    ],
  },
];

interface NotificationCounts {
  chats: number;
  support: number;
  feedback: number;
}

export const Sidebar: React.FC = () => {
  const [logoSrc, setLogoSrc] = useState<string>('/fahryo_logo.png');
  const [logoHeight, setLogoHeight] = useState<number>(32);
  const [showChangelog, setShowChangelog] = useState(false);
  const [badges, setBadges] = useState<NotificationCounts>({ chats: 0, support: 0, feedback: 0 });
  const appVersion = useAppVersion();

  // Fetch logo settings
  useEffect(() => {
    const fetchLogoSettings = async () => {
      try {
        const { data } = await supabase
          .from('app_settings')
          .select('key, value')
          .in('key', ['logo_url', 'logo_height_admin']);
        
        if (data) {
          for (const row of data) {
            const rawVal = row.value;
            const val = (() => { try { return typeof rawVal === 'string' ? JSON.parse(rawVal) : String(rawVal); } catch { return typeof rawVal === 'string' ? rawVal.replace(/^"|"$/g, '') : String(rawVal); } })();
            if (row.key === 'logo_url') {
              if (val.startsWith('http')) {
                setLogoSrc(val);
              } else if (val === 'fahryo_logo.png' || val === '/fahryo_logo.png') {
                setLogoSrc('/fahryo_logo.png');
              } else {
                setLogoSrc(`${SUPABASE_URL}/storage/v1/object/public/logos/${val}`);
              }
            }
            if (row.key === 'logo_height_admin') {
              setLogoHeight(parseInt(val) || 32);
            }
          }
        }
      } catch {
        // Use defaults
      }
    };
    fetchLogoSettings();
  }, []);

  // Poll notification counts every 30 seconds
  const fetchBadges = useCallback(async () => {
    try {
      const res = await adminFetch('/api/support-chat/admin/notifications');
      if (res.ok) {
        const data = await res.json();
        setBadges({
          chats: data.chats || 0,
          support: data.support || 0,
          feedback: data.feedback || 0,
        });
      }
    } catch {
      // Silently fail — badges are non-critical
    }
  }, []);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 30_000);
    return () => clearInterval(interval);
  }, [fetchBadges]);

  return (
    <aside className="admin-sidebar">
      <div className="sidebar-logo">
        <img
          src={logoSrc}
          alt="Fahryo"
          style={{ width: logoHeight, height: logoHeight, borderRadius: 8, objectFit: 'contain' }}
          onError={(e) => { (e.target as HTMLImageElement).src = '/fahryo_logo.png'; }}
        />
        <div>
          <h2>Fahryo</h2>
          <span>Admin</span>
          <span onClick={() => setShowChangelog(true)} style={{ display: 'block', fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 2, fontWeight: 500, letterSpacing: '0.03em', cursor: 'pointer', transition: 'color 0.15s' }} onMouseEnter={e => { (e.target as HTMLElement).style.color = 'rgba(255,255,255,0.7)'; }} onMouseLeave={e => { (e.target as HTMLElement).style.color = 'rgba(255,255,255,0.35)'; }} title="Changelog anzeigen">v{appVersion}</span>
        </div>
      </div>

      <nav style={{ flex: 1, padding: '4px 0' }}>
        {navigation.map((section, sIdx) => (
          <div key={sIdx} className="sidebar-section">
            {section.title && (
              <div className="sidebar-section-label">{section.title}</div>
            )}
            {section.items.map((item) => {
              const badgeCount = item.badgeKey ? badges[item.badgeKey as keyof NotificationCounts] || 0 : 0;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `sidebar-item ${isActive ? 'active' : ''}`
                  }
                >
                  {item.icon}
                  {item.label}
                  {badgeCount > 0 && (
                    <span style={{
                      marginLeft: 'auto',
                      background: '#ef4444',
                      color: '#fff',
                      borderRadius: 999,
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '1px 6px',
                      minWidth: 18,
                      textAlign: 'center',
                      lineHeight: '16px',
                      animation: 'pulse-badge 2s ease-in-out infinite',
                    }}>
                      {badgeCount}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <style>{`
        @keyframes pulse-badge {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>

      <ChangelogModal isOpen={showChangelog} onClose={() => setShowChangelog(false)} />
    </aside>
  );
};
